import { useState, type FormEvent } from 'react';
import { useGameStore } from '@campus/core';
import { getTenantSlug } from '../../lib/tenant.js';
import { API_BASE } from '../../lib/apiBase.js';
import { Button, Icon, Input, LogoMark, PasswordInput } from '../ds/index.js';

/** Codes renvoyés par POST /auth/login (`{ error: CODE }`) → message affiché. */
const LOGIN_ERRORS: Record<string, string> = {
  INVALID_CREDENTIALS: 'Adresse e-mail ou mot de passe incorrect.',
  TOO_MANY_ATTEMPTS:   'Trop de tentatives. Réessaie dans 15 minutes.',
  ACCOUNT_BANNED:      "Ce compte a été suspendu. Contacte un administrateur de l'espace.",
  NOT_A_MEMBER:        "Ce compte n'a pas accès à cet espace.",
  MEMBER_NOT_FOUND:    "Ce compte n'a pas accès à cet espace.",
};
const LOGIN_ERROR_FALLBACK = 'Connexion impossible. Vérifie ta connexion internet et réessaie.';

/**
 * Lien permanent « Activer mon compte » (/#activer), à partager aux nouveaux
 * membres : même envoi que « Mot de passe oublié » — un compte jamais activé
 * reçoit une nouvelle invitation, un compte actif un lien de réinitialisation.
 * Le mot de passe se choisit toujours depuis le lien reçu par e-mail : taper
 * une adresse ne prouve pas qu'on en est le titulaire.
 */
const ACTIVATE_HASH = '#activer';

type EmailFlow = 'forgot' | 'activate';

const EMAIL_FLOW_TEXT: Record<EmailFlow, { subtitle: string; intro: string; sent: string }> = {
  forgot: {
    subtitle: 'Mot de passe oublié',
    intro: "Indique ton adresse e-mail : on t'envoie un lien pour choisir un nouveau mot de passe.",
    sent: 'Si un compte existe pour cette adresse, tu vas recevoir un e-mail avec un lien pour choisir un nouveau mot de passe.',
  },
  activate: {
    subtitle: 'Activer mon compte',
    intro: "Indique l'adresse e-mail avec laquelle ton compte a été créé : on t'envoie un lien pour choisir ton mot de passe.",
    sent: "Si un compte existe pour cette adresse, tu vas recevoir un e-mail avec un lien pour choisir ton mot de passe. Pense à regarder dans tes indésirables.",
  },
};

/**
 * LoginPage — Formulaire email + mot de passe.
 * JWT stocké en mémoire Zustand uniquement (jamais localStorage).
 * Si displayName existe déjà → 'in-game' direct. Sinon → AvatarPage.
 */
export function LoginPage() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);

  const [emailFlow, setEmailFlow] = useState<EmailFlow | null>(
    () => (window.location.hash === ACTIVATE_HASH ? 'activate' : null),
  );
  const [forgotEmail, setForgotEmail]     = useState('');
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const setJwt        = useGameStore((s) => s.setJwt);
  const setAuthState  = useGameStore((s) => s.setAuthState);
  const setProfile    = useGameStore((s) => s.setProfile);
  const setAppearance = useGameStore((s) => s.setAppearance);
  const setTargetZoneSlug  = useGameStore((s) => s.setTargetZoneSlug);
  const setTargetSpawnName = useGameStore((s) => s.setTargetSpawnName);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const tenantSlug = getTenantSlug();
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantSlug,
        },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        setError((body.error && LOGIN_ERRORS[body.error]) ?? LOGIN_ERROR_FALLBACK);
        return;
      }
      const data = await res.json() as { token: string; displayName?: string; avatarIndex?: number; appearance?: Record<string, unknown> | null };
      // Reset la zone cible d'une eventuelle session precedente dans le meme
      // onglet — sinon un nouveau login herite de la derniere zone visitee
      // par le compte precedent (targetZoneSlug n'est jamais reset ailleurs).
      setTargetZoneSlug(''); // carte d'arrivée du campus
      setTargetSpawnName(null);
      setJwt(data.token);
      const name = data.displayName ?? email.split('@')[0] ?? 'Anonyme';
      setProfile(name, data.avatarIndex ?? 0);
      if (data.appearance) {
        setAppearance(data.appearance as any);
      }
      // Skip AvatarPage if user already has a displayName and appearance —
      // passe quand meme par le lobby (choix des peripheriques avant d'entrer).
      const needsOnboarding = !data.displayName || data.displayName.trim() === '' || !data.appearance;
      setAuthState(needsOnboarding ? 'authenticated' : 'lobby');
    } catch {
      setError(LOGIN_ERROR_FALLBACK);
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: FormEvent) {
    e.preventDefault();
    if (forgotLoading) return;
    setForgotLoading(true);
    try {
      const tenantSlug = getTenantSlug();
      await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantSlug,
        },
        body: JSON.stringify({ email: forgotEmail }),
      });
    } finally {
      // Réponse toujours générique côté API — on l'affiche que la requête
      // réseau ait réussi ou non, jamais d'info sur l'existence du compte.
      setForgotSubmitted(true);
      setForgotLoading(false);
    }
  }

  function openEmailFlow(flow: EmailFlow) {
    setForgotEmail(email);
    setForgotSubmitted(false);
    setEmailFlow(flow);
  }

  function backToLogin() {
    setEmailFlow(null);
    if (window.location.hash === ACTIVATE_HASH) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  return (
    <div
      className="pl-page"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-6)',
        width: '100vw',
        height: '100dvh',
        boxSizing: 'border-box',
        padding: 'var(--space-4)',
        overflowY: 'auto',
      }}
    >
      <main
        style={{
          width: '100%',
          maxWidth: 400,
          boxSizing: 'border-box',
          padding: 'var(--space-10) var(--space-8) var(--space-8)',
          background: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-8)',
        }}
      >
        <header style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-2)', textAlign: 'center' }}>
          <div style={{ marginBottom: 'var(--space-2)' }}><LogoMark size={100} /></div>
          <h1 style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-extrabold)' as unknown as number,
            fontSize: 'var(--text-3xl)',
            lineHeight: 'var(--leading-tight)',
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--color-text-primary)',
          }}>
            Placio
          </h1>
          <p style={{ margin: 0, fontSize: 'var(--text-base)', lineHeight: 'var(--leading-normal)', color: 'var(--color-text-secondary)' }}>
            {emailFlow ? EMAIL_FLOW_TEXT[emailFlow].subtitle : 'Connecte-toi à ton espace'}
          </p>
        </header>

        {!emailFlow ? (
          <>
            <form
              onSubmit={(e) => { void handleSubmit(e); }}
              noValidate
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
            >
              <Input
                label="Adresse e-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ton email"
                required
                autoComplete="email"
              />
              <PasswordInput
                label="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              {error && (
                <div className="pl-alert" role="alert">
                  <Icon name="alert" size={16} />
                  <span>{error}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-2)' }}>
                <Button variant="primary" type="submit" disabled={loading} style={{ minWidth: 200 }}>
                  {loading ? 'Connexion…' : 'Se connecter'}
                </Button>
              </div>
            </form>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <Button variant="ghost" size="sm" onClick={() => openEmailFlow('forgot')}>
                Mot de passe oublié ?
              </Button>
              <Button variant="ghost" size="sm" onClick={() => openEmailFlow('activate')}>
                Première connexion ?
              </Button>
            </div>
          </>
        ) : forgotSubmitted ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className="pl-alert pl-alert-success" role="status">
              <Icon name="check" size={16} />
              <span>{EMAIL_FLOW_TEXT[emailFlow].sent}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="secondary" icon="arrow-left" onClick={backToLogin}>
                Retour à la connexion
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => { void handleForgotPassword(e); }}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
          >
            <p style={{ margin: 0, fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-normal)', color: 'var(--color-text-secondary)' }}>
              {EMAIL_FLOW_TEXT[emailFlow].intro}
            </p>
            <Input
              label="Adresse e-mail"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="ton email"
              required
              autoComplete="email"
              autoFocus
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <Button variant="ghost" onClick={backToLogin}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={forgotLoading}>
                {forgotLoading ? 'Envoi…' : 'Envoyer le lien'}
              </Button>
            </div>
          </form>
        )}
      </main>
      <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--color-text-muted)' }}>
        Placio · Ton espace virtuel
      </p>
    </div>
  );
}
